import { ThemeSwitcher } from '../theme-switcher'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FieldError, Input, Wordmark } from '@speaking-coach/ui'
import { AdminApiError } from '../api/client.js'
import { FormError } from '../common/field-errors.js'
import { SubmitButton } from '../common/save-button.js'
import { resolveWebRecoveryUrl } from './links.js'
import { useAdminAuth } from './use-admin-auth.js'

const adminLoginSchema = z.object({
  email: z.string().trim().email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
})

type AdminLoginForm = z.infer<typeof adminLoginSchema>

function messageForLoginError(error: unknown): string {
  if (error instanceof AdminApiError) {
    if (error.detail === 'invalid_credentials') {
      return 'Email or password is incorrect.'
    }
    if (error.code === 'RATE_LIMITED' || error.detail === 'rate_limited') {
      return 'Too many sign-in attempts. Wait a moment and try again.'
    }
    if (error.code === 'UNPROCESSABLE') {
      return 'Check your email address and password and try again.'
    }
    if (error.code === 'SERVER_ERROR' || error.code === 'PROVIDER_ERROR') {
      return 'Sign-in is temporarily unavailable. Try again in a moment.'
    }
  }
  return 'Could not sign in. Check your connection and try again.'
}

/** The Admin origin's own password sign-in surface. */
export function AdminLoginPage(): JSX.Element {
  const { login } = useAdminAuth()
  const [failure, setFailure] = useState<string | null>(null)
  const recoveryUrl = resolveWebRecoveryUrl()
  const form = useForm<AdminLoginForm>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: { email: '', password: '' },
  })

  async function submit(values: AdminLoginForm): Promise<void> {
    setFailure(null)
    try {
      await login(values.email, values.password)
    } catch (error) {
      setFailure(messageForLoginError(error))
    }
  }

  const emailError = form.formState.errors.email?.message ?? null
  const passwordError = form.formState.errors.password?.message ?? null

  return (
    <main className="flex min-h-svh items-start justify-center bg-surface-app px-5 py-12 text-text-primary sm:items-center sm:px-6">
      <div className="w-full max-w-[420px] space-y-5">
        <form
          aria-label="Cooori Admin sign in"
          className="w-full max-w-[420px] space-y-5 rounded-token-card-lg border border-border-default bg-surface-card p-6 sm:p-8"
          noValidate
          onSubmit={form.handleSubmit(submit)}
        >
          <header className="space-y-6">
            <Wordmark height={20} />
            <h1 className="text-section-title font-semibold">Sign in to Admin</h1>
          </header>

          <div>
            <label className="mb-1.5 block text-body font-medium" htmlFor="admin-email">
              Email
            </label>
            <Input
              id="admin-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoFocus
              aria-invalid={emailError !== null}
              aria-describedby={emailError ? 'admin-email-error' : undefined}
              {...form.register('email')}
            />
            <FieldError id="admin-email-error" message={emailError} />
          </div>

          <div>
            <label className="mb-1.5 block text-body font-medium" htmlFor="admin-password">
              Password
            </label>
            <Input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              aria-invalid={passwordError !== null}
              aria-describedby={passwordError ? 'admin-password-error' : undefined}
              {...form.register('password')}
            />
            <FieldError id="admin-password-error" message={passwordError} />
          </div>

          {/* Above the button, as on Student and Manager sign-in: two fields
              leave nothing below the fold. */}
          <FormError message={failure} />
          <SubmitButton
            className="w-full"
            size="field"
            pending={form.formState.isSubmitting}
            pendingLabel="Signing in…"
          >
            Sign in
          </SubmitButton>
          {recoveryUrl ? (
            <div className="text-center">
              <a
                className="text-body text-text-primary hover:underline"
                href={recoveryUrl}
              >
                Can&apos;t sign in?
              </a>
            </div>
          ) : null}
        </form>
        <div className="flex justify-center"><ThemeSwitcher surface="app" /></div>
      </div>
    </main>
  )
}
